import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";
import { STATION_TIME_ZONE } from "../../../../util/stationTime";
import { parseBookingQuery } from "../common/url/bookingQuery";

/**
 * The zone a schedule from the URL is read in (#3219), or `null` while it is
 * not known yet.
 *
 * It is the LOCATION's zone: the server validates the window on that clock,
 * so a date and time from the URL mean the location's wall clock. When the URL
 * names a location, hydration therefore waits until that location is the
 * selected one (`useLocationHydration`) — reading the schedule before it would
 * pin the picked moments to the default zone.
 *
 * - URL without a location: the default zone.
 * - Location hydration has FINISHED (`locationHydrated`) and the URL's
 *   location matched none of the tenant's locations: the default zone too, so
 *   the schedule is still hydrated (as before #3219) rather than dropped.
 *
 * The URL's own `timezone` parameter is NOT honoured: the server never reads
 * the window in a zone the client names, so a link carrying another zone
 * would pin moments the server then reads differently.
 */
export function useHydrationTimeZone(
  search: string,
  locationHydrated: boolean = false
): string | null {
  const selectedLocationId = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation?.id ?? null
  );
  const locationZone = useSelectedLocationTimeZone();
  const query = useMemo(() => parseBookingQuery(search), [search]);

  if (!query.locationId) return STATION_TIME_ZONE;
  if (selectedLocationId === query.locationId) return locationZone;
  return locationHydrated ? STATION_TIME_ZONE : null;
}
